import * as React from 'react';

import type { Cml_SubtitleTrack as SubtitleTrack } from '__generated__/graphql-types-do-not-use';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import { AssetTypeNames } from 'bundles/asset-admin/types/assets';
import { EmbeddablePdfAsset } from 'bundles/cml/shared/components/asset/EmbeddablePdfAsset';
import NonMediaAsset from 'bundles/cml/shared/components/asset/NonMediaAsset';
import {
  isPDfEmbedEnabled,
  isSupportedAudioAsset,
  isSupportedVideoAsset,
} from 'bundles/cml/shared/components/asset/assetTypeUtils';
import type { VideoResolutions } from 'bundles/cml/shared/components/asset/types';
import type { AssetData } from 'bundles/cml/shared/types/assetDataTypes';
import type { AssetElement } from 'bundles/cml/shared/types/elementTypes';

const VideoAsset = createLoadableComponent(() => import('bundles/cml/shared/components/asset/VideoAsset'));
const AudioAsset = createLoadableComponent(() => import('bundles/cml/shared/components/asset/AudioAsset'));

type AssetProps = {
  assetData: AssetData;
  assetElement: AssetElement;
  readonly?: boolean;
};

type VideoAssetProps = AssetProps & {
  videoResolutions: VideoResolutions;
  subtitleTracks: SubtitleTrack[];
};

export const isAssetPropsVideo = (assetProps: AssetProps): assetProps is VideoAssetProps => {
  return assetProps.assetData?.type === AssetTypeNames.VIDEO;
};

const AssetContent = (props: Omit<AssetProps, 'onOpenMenu'>) => {
  const { assetElement, assetData, readonly } = props;
  const { id } = assetElement;

  // key is necessary to re-mount the component once asset data is loaded
  const nodeKey = `asset-node-${id}`;

  if (isSupportedAudioAsset(assetData)) {
    return <AudioAsset key={nodeKey} id={id} assetData={assetData} />;
  }

  if (isAssetPropsVideo(props) && isSupportedVideoAsset(assetData)) {
    return (
      <VideoAsset
        key={nodeKey}
        id={id}
        assetData={assetData}
        subtitleTracks={props.subtitleTracks}
        videoResolutions={props.videoResolutions}
      />
    );
  }

  if (assetElement && isPDfEmbedEnabled(assetElement)) {
    return <EmbeddablePdfAsset assetData={assetData} assetElement={assetElement} />;
  }

  return <NonMediaAsset key={nodeKey} id={id} assetData={assetData} readonly={readonly} />;
};

export default AssetContent;
